import axios, { AxiosInstance } from "axios";
import { useAuth0 } from "@auth0/auth0-react";

// Create axios instance for AI backend
const createAiAxiosClient = (getAccessTokenSilently: () => Promise<string>): AxiosInstance => {
  const aiAxiosClient = axios.create({
    baseURL: process.env.REACT_APP_BACKEND_AI_ENDPOINT || "http://localhost:3000",
    timeout: 30000, // 30 seconds timeout
    headers: {
      "Content-Type": "application/json",
      "req-sent-from": "opsAdmin", // Consistent with existing Apollo client
    },
  });

  // Request interceptor to add Auth0 token
  aiAxiosClient.interceptors.request.use(
    async (config) => {
      try {
        const token = await getAccessTokenSilently();
        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.error("Error getting access token:", error);
        // You might want to handle this differently based on your needs
        // For now, we'll continue without the token
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  aiAxiosClient.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (error.response?.status === 401) {
        // Token might be expired, Auth0 will handle refresh automatically
        console.warn("Unauthorized request - token might be expired");
      }
      return Promise.reject(error);
    }
  );

  return aiAxiosClient;
};

// Hook to get the configured axios instance
export const useAiAxiosClient = (): AxiosInstance => {
  const { getAccessTokenSilently } = useAuth0();
  return createAiAxiosClient(getAccessTokenSilently);
};

// Export the factory function for use outside of React components
export { createAiAxiosClient };

// Export a default instance that can be used with manual token injection
export const aiAxiosClient = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_AI_ENDPOINT || "http://localhost:3000",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    "req-sent-from": "opsAdmin",
  },
});

// Helper function to manually set token for the default instance
export const setAiAxiosToken = async (getAccessTokenSilently: () => Promise<string>) => {
  try {
    const token = await getAccessTokenSilently();
    aiAxiosClient.defaults.headers.Authorization = `Bearer ${token}`;
  } catch (error) {
    console.error("Error setting AI axios token:", error);
  }
};

export default aiAxiosClient;
